'use client';

import ForBrokersIcon from '@icons/ForBrokers.svg';
import RewardIcon from '@icons/reward.svg';
import TrendingIcon from '@icons/trend.svg';
import GeneralAgentsHeroImage from '@images/use-cases/GasImage.webp';
import { Containers, Widgets } from '@components';
import { CompanySubnav } from '@/components/elements/CompanySubnav';
import LostRevenueIcon from '@icons/LostRevenue.svg';
import ProtectYourRoleIcon from '@icons/ProtectYourRole.svg';
import {
    GENERAL_AGENTS_USE_CASES_WHY_HOW_BLOCKS,
    GENERAL_AGENTS_USE_CASES_WHY_HOW_SECTION_TITLE,
} from '@/utils/content/generalAgentsWhyHow';

export default function GeneralAgentsPageUseCase() {
    return (
        <>
            <CompanySubnav section="use-cases" currentLabel="General Agents" />
            <Containers.Section
                el={
                    <Widgets.UseCasesHero
                        badge={{
                            iconSrc: ForBrokersIcon,
                            label: 'Built for General Agents',
                        }}
                        title="GAs: General Agents"
                        description="For a General Agent (GA), the business model is built on being the 'Broker’s Broker.' You provide the back-office, the carrier access, and the expertise. However, General Agents are currently facing a massive threat: Turnkey ICHRA platforms are bypassing GAs and going direct to brokers (or even direct to employers), effectively cutting the GA and broker out of the distribution chain."
                        secondaryDescription="By white-labeling Predictable Benefits, a GA reclaims their position as the essential hub for their broker network."
                        image={{
                            src: GeneralAgentsHeroImage,
                            alt: 'A smiling professional in a blue shirt at a desk in a bright office, writing in a notebook.',
                            priority: true,
                        }}
                        overlayCardsPositionClassName="bottom-[65%] right-3 md:bottom-[70%] md:right-4 lg:bottom-[60%] lg:right-3 lg:-translate-x-80"
                        overlayCards={[
                            {
                                title: 'Capture Lost Revenue',
                                subtitle:
                                    'Turn declined clients into new opportunities',
                                iconSrc: LostRevenueIcon,
                                className: 'w-full max-w-[320px] md:max-w-[320px] lg:max-w-[320px] shrink-0',
                            },
                            {
                                title: 'Protect Your Role',
                                subtitle: 'Stay central in the benefits ecosystem',
                                iconSrc: ProtectYourRoleIcon,
                                className: 'w-full max-w-[290px] md:max-w-[300px] lg:max-w-[300px] shrink-0',
                            },
                        ]}
                    />
                }
            />
            <Containers.Section el={<Widgets.HeroOvalDivider color="brand-50" />} />
            <Containers.Section
                el={
                    <Widgets.UseCasesWhyHow
                        sectionTitle={
                            GENERAL_AGENTS_USE_CASES_WHY_HOW_SECTION_TITLE
                        }
                        blocks={GENERAL_AGENTS_USE_CASES_WHY_HOW_BLOCKS}
                        pillNavMode="fromBlocks"
                    />
                }
            />

            <Containers.Section
                el={
                    <Widgets.ThePivotScript
                        eyebrow="SOLUTION"
                        title='The "Protect Your Book" Pivot Script'
                        description='Scenario: A GA Sales Rep is talking to one of their top-producing brokers who just mentioned they are looking at a tech-heavy ICHRA platform to solve a high-renewal problem for a client.'
                        cards={[
                            {
                                iconSrc: TrendingIcon,
                                iconAlt: 'The Pivot Script',
                                title: 'The Value Re-Assertion',
                                description: 'GA Rep: "I’ve seen those platforms. They have great marketing, but they don\'t have the local market expertise or the compliance backbone that we provide. Plus, when you use a "direct" platform, you’re often handing over your client data to a company that eventually wants to be a broker themselves."',
                            },
                            {
                                iconSrc: TrendingIcon,
                                iconAlt: 'The Pivot Script',
                                title: 'The Integrated Solution',
                                description: 'GA Rep: "That’s why we’ve launched our own white-labeled marketplace. You get that same \"slick\" shopping experience for your clients, but it’s powered by us. It’s the [GA Name] Portal—you stay the hero, and we handle the back-end admin, carrier feeds, and compliance."',
                            },
                            {
                                iconSrc: TrendingIcon,
                                iconAlt: 'The Pivot Script',
                                title: 'The "No-Brainer" Expansion',
                                description: 'GA Rep: "With this tool, you can finally go after those 5-person shops you used to turn away. You can quote an ICHRA in minutes, and we\'ll handle the individual policy enrollments across all carriers (BlueCross, United, Oscar, etc.) in one place. It makes the individual market feel like a group plan for you."',
                            },
                            {
                                iconSrc: TrendingIcon,
                                iconAlt: 'The Pivot Script',
                                title: 'The Closing Hook',
                                description: 'GA Rep: "Don\'t send your business to a tech startup that doesn\'t know your name. Use our platform, keep your commissions higher, and let’s run a demo for your next \"un-insurable\" group on Monday. What do you think?"',
                            },
                        ]}
                    />
                }
            />
            <Containers.Section
                el={
                    <Widgets.ExploreOtherUseCases excludeSlug="general-agents" />
                }
            />
            <Containers.Section el={<Widgets.ReadToBuild />} />
        </>
    );
}
